import { MONTHS, ProjectionMonthlyTargetData, TargetType } from './kinerja.types';

interface ProyeksiInputTableProps {
  targetType: TargetType;
  projectionYears: number[];
  projectionMonthlyData: ProjectionMonthlyTargetData[];
  isReadOnly?: boolean;
  onChange: (month: number, year: number, value: number) => void;
}

function formatDisplayValue(value: number | undefined): string {
  return value ? value.toLocaleString('id-ID') : '';
}

function parseRawInput(value: string): number {
  const digitsOnly = value
    .split('')
    .filter(char => /\d/.test(char))
    .join('');
  return Number.parseInt(digitsOnly, 10) || 0;
}

export default function ProyeksiInputTable({
  targetType,
  projectionYears,
  projectionMonthlyData,
  isReadOnly = false,
  onChange,
}: Readonly<ProyeksiInputTableProps>) {
  const yearTotals = projectionYears.map((_, yearIndex) =>
    projectionMonthlyData.reduce((acc, d) => acc + (d.values[yearIndex] ?? 0), 0),
  );

  return (
    <div className="mt-6">
      <h3 className="text-base font-semibold text-gray-800 dark:text-white text-center mb-4">
        Target Pertumbuhan Omzet yang Ingin Dicapai dalam 5 Tahun
      </h3>

      <div className="flex items-center gap-1 mb-2">
        <span className="text-xs text-gray-400 dark:text-gray-500">&#9432;</span>
        <span className="text-xs text-gray-500 dark:text-gray-400">Target Omzet 5 Tahun Ke Depan</span>
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
        <table className="min-w-[640px] w-full text-sm">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-300 dark:border-gray-600">
              <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-white w-28">Tahun</th>
              {projectionYears.map(year => (
                <th key={year} className="px-4 py-3 text-center font-semibold text-gray-700 dark:text-white">
                  Target Omzet {year}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {MONTHS.map((monthName, idx) => {
              const month = idx + 1;
              const monthData = projectionMonthlyData.find(d => d.month === month);
              const values = monthData?.values ?? [];

              return (
                <tr
                  key={month}
                  className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50/50 dark:hover:bg-gray-700/50"
                >
                  <td className="px-4 py-2 font-medium text-gray-700 dark:text-white">{monthName}</td>
                  {projectionYears.map((year, yearIndex) => {
                    const value = values[yearIndex];

                    if (isReadOnly) {
                      return (
                        <td key={year} className="px-4 py-2 text-center text-gray-600 dark:text-gray-300">
                          {targetType === 'rupiah' && value ? 'Rp ' : ''}
                          {formatDisplayValue(value) || '-'}
                        </td>
                      );
                    }

                    return (
                      <td key={year} className="px-3 py-2">
                        <div className="flex items-center gap-1">
                          {targetType === 'rupiah' && (
                            <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">Rp</span>
                          )}
                          <input
                            type="text"
                            inputMode="numeric"
                            value={formatDisplayValue(value)}
                            onChange={e => onChange(month, year, parseRawInput(e.target.value))}
                            className="w-full min-w-0 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-right"
                            placeholder="0"
                          />
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}

            {/* Total row */}
            <tr className="bg-gray-50 dark:bg-gray-800 border-t-2 border-gray-300 dark:border-gray-600">
              <td className="px-4 py-3 font-bold text-gray-800 dark:text-white">Total</td>
              {yearTotals.map((total, yearIndex) => (
                <td
                  key={projectionYears[yearIndex]}
                  className="px-4 py-3 text-right font-semibold text-gray-800 dark:text-white"
                >
                  {targetType === 'rupiah' && total ? 'Rp ' : ''}
                  {formatDisplayValue(total) || '-'}
                </td>
              ))}
            </tr>

            {/* Rata-rata row */}
            <tr className="bg-gray-50 dark:bg-gray-800 border-t-2 border-gray-300 dark:border-gray-600">
              <td className="px-4 py-3 font-bold text-gray-800 dark:text-white">Rata-rata</td>
              {yearTotals.map((total, yearIndex) => (
                <td
                  key={projectionYears[yearIndex]}
                  className="px-4 py-3 text-right font-semibold text-gray-800 dark:text-white"
                >
                  {targetType === 'rupiah' && total ? 'Rp ' : ''}
                  {formatDisplayValue(Math.round(total / 12)) || '-'}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
